import React from 'react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { History, CheckCircle2, ChevronRight, Clock } from 'lucide-react';

export default function GestaoLotesHistorico({
  open,
  onOpenChange,
  lote,
  registos = [],
  loading = false,
  construirHistorico,
  getCategoriaCor,
}) {
  if (!lote) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-stone-600" />
            Histórico do Lote {lote.codigo_lote || `L${lote.numero_lote}`}
          </DialogTitle>
          <DialogDescription>
            Rastreabilidade completa das movimentações deste lote.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Resumo rápido */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-stone-50 rounded-lg text-center">
              <p className="text-xs text-stone-500 mb-1">Quantidade</p>
              <p className="text-xl font-bold text-navy">{lote.quantidade_ajustada || lote.quantidade}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg text-center">
              <p className="text-xs text-stone-500 mb-1">Produzidas</p>
              <p className="text-xl font-bold text-emerald-700">{lote.quantidade_produzida || 0}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-xs text-stone-500 mb-1">Registos</p>
              <p className="text-xl font-bold text-blue-700">{loading ? '...' : registos.length}</p>
            </div>
          </div>

          {/* Movimentações */}
          <div>
            <h4 className="text-sm font-semibold text-stone-600 uppercase tracking-wide mb-3">Movimentações de Categoria</h4>
            <div className="relative">
              {(construirHistorico ? construirHistorico(lote) : []).map((item, index, arr) => (
                <div key={index} className="flex gap-4 pb-4 relative">
                  {index < arr.length - 1 && (
                    <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-stone-200" />
                  )}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    item.status === 'aprovado' ? 'bg-green-100 text-green-600' :
                    item.status === 'pendente' ? 'bg-amber-100 text-amber-600' :
                    'bg-stone-200 text-stone-500'
                  }`}>
                    {index === arr.length - 1 ? <CheckCircle2 className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </div>
                  <div className={`flex-1 bg-white rounded-lg border p-3 ${index === arr.length - 1 ? 'border-green-200 bg-green-50/30' : 'border-stone-200'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`font-bold ${index === arr.length - 1 ? 'text-green-700' : 'text-stone-700'}`}>{item.codigo}</span>
                      {item.categoria_codigo && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${getCategoriaCor ? getCategoriaCor(item.categoria_codigo) : ''}`}>{item.categoria}</span>
                      )}
                    </div>
                    <div className="text-sm text-stone-600 mb-1">
                      {item.quantidade !== '?' && <span className="font-medium">{item.quantidade} peças</span>}
                    </div>
                    {item.data && <p className="text-xs text-stone-400">{new Date(item.data).toLocaleString('pt-PT')}</p>}
                  </div>
                </div>
              ))}
            </div>
            {lote.lotes_origem_codigos && lote.lotes_origem_codigos.length > 0 && (
              <div className="text-xs text-stone-500 mt-2 pt-2 border-t">
                <span className="font-medium">Lote(s) de origem:</span> {lote.lotes_origem_codigos.join(', ')}
              </div>
            )}
          </div>

          {/* Registos de Produção */}
          <div>
            <h4 className="text-sm font-semibold text-stone-600 uppercase tracking-wide mb-3">Registos de Produção</h4>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
              </div>
            ) : registos.length === 0 ? (
              <p className="text-stone-400 text-sm text-center py-4">Nenhum registo de produção encontrado</p>
            ) : (
              <div className="space-y-2">
                {registos.map((reg, i) => {
                  const duracaoMin = reg.tempo_total_segundos ? Math.round(reg.tempo_total_segundos / 60) : null;
                  const eficiencia = reg.eficiencia_percentual ? Math.round(reg.eficiencia_percentual) : null;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-stone-200 text-sm">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-semibold text-primary">{reg.operacao_codigo || 'OP'}</span>
                          <span className="font-medium text-stone-700 truncate">{reg.operacao_nome || '—'}</span>
                        </div>
                        <p className="text-xs text-stone-500 mt-0.5">
                          {reg.operador_nome || reg.operador_id}
                          {reg.inicio && <span> · {new Date(reg.inicio).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-emerald-700">{reg.quantidade_produzida || 0} un.</p>
                        {duracaoMin !== null && <p className="text-xs text-stone-400">{duracaoMin} min</p>}
                        {eficiencia !== null && (
                          <p className={`text-xs font-medium ${eficiencia >= 90 ? 'text-emerald-600' : eficiencia >= 70 ? 'text-amber-600' : 'text-red-500'}`}>
                            {eficiencia}%
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
